export const FORM_META_KEYS = new Set([
  'form_filled',
  'visa_name',
  'form_sections',
  'form_params',
  'form_field_labels',
  'form_version',
]);

export const parseApplicationData = (data) => {
  if (data == null) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof data === 'object') return data;
  return null;
};

export const isFormFilled = (data) => {
  const parsed = parseApplicationData(data);
  if (!parsed || typeof parsed !== 'object') return false;
  if (parsed.form_filled === false) return false;
  if (parsed.form_filled === true) return true;

  return Object.entries(parsed).some(([key, value]) => {
    if (FORM_META_KEYS.has(key)) return false;
    return value != null && String(value).trim() !== '';
  });
};

const formatFieldKeyAsLabel = (key) => {
  const str = String(key);
  if (/^\d+$/.test(str)) return `Field ${str}`;
  return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const lookupFieldLabel = (labelMap, key) => {
  if (!labelMap || typeof labelMap !== 'object') return null;
  const strKey = String(key);
  return labelMap[strKey] ?? labelMap[key] ?? null;
};

export const hasUsableFormFieldLabels = (data) => {
  const parsed = parseApplicationData(data);
  const labels = parsed?.form_field_labels;
  if (!labels || typeof labels !== 'object' || Object.keys(labels).length === 0) {
    return false;
  }

  return Object.entries(labels).some(([key, label]) => {
    const labelText = String(label || '').trim();
    return labelText && labelText !== String(key) && !/^\d+$/.test(labelText);
  });
};

export const getFormDisplayEntries = (data, extraLabels = {}) => {
  const parsed = parseApplicationData(data);
  if (!parsed || typeof parsed !== 'object') return [];

  const labelMap = {
    ...(parsed.form_field_labels && typeof parsed.form_field_labels === 'object'
      ? parsed.form_field_labels
      : {}),
    ...extraLabels,
  };

  return Object.entries(parsed)
    .filter(([key]) => !FORM_META_KEYS.has(key))
    .map(([key, value]) => ({
      key,
      label: lookupFieldLabel(labelMap, key) || formatFieldKeyAsLabel(key),
      value: value == null || value === '' ? '-' : String(value),
    }));
};

export const needsFormLabelResolution = (data) => {
  const parsed = parseApplicationData(data);
  if (!parsed) return false;
  if (hasUsableFormFieldLabels(parsed)) return false;

  return Object.keys(parsed).some((key) => !FORM_META_KEYS.has(key));
};

export const isCompleteFormParams = (params) =>
  Boolean(
    params?.gid &&
      params?.visatype &&
      params?.visaid &&
      params?.origincountry &&
      params?.destinationcountry
  );

export const getApplicationFormParams = (application) => {
  const parsed = parseApplicationData(application?.customer_visa_application_data);

  if (isCompleteFormParams(application?.form_params)) {
    return application.form_params;
  }

  if (isCompleteFormParams(parsed?.form_params)) {
    return parsed.form_params;
  }

  // Incomplete stored params are still useful as a starting point for the meta API.
  if (parsed?.form_params && typeof parsed.form_params === 'object') {
    return parsed.form_params;
  }

  return null;
};

export const getInitialFormValues = (application) => {
  const parsed = parseApplicationData(application?.customer_visa_application_data);
  if (!parsed || typeof parsed !== 'object') return {};

  return Object.fromEntries(
    Object.entries(parsed).filter(([key]) => !FORM_META_KEYS.has(key))
  );
};
